import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator, ChevronDown, ChevronUp } from "lucide-react";
import { formatCOP } from "@/lib/api";
import MoneyInput from "@/components/MoneyInput";

/**
 * Calculadora reutilizable de costeo real.
 *
 * Fórmula:
 *   costo_real     = costo_base_cop × (1 + %gastos)
 *   precio_minimo  = costo_real × (1 + %margen_mínimo)
 *   precio_sugerido= costo_real × (1 + %margen_sugerido)
 *
 * onApply({ costo, precio_minimo, precio_sugerido }) -> se dispara al aplicar valores.
 */
export default function CosteoCalculator({ defaults, onApply }) {
  const [open, setOpen] = useState(false);
  const [usd, setUsd] = useState("");
  const [trm, setTrm] = useState(defaults?.trm || 4200);
  const [pctGastos, setPctGastos] = useState(defaults?.pct_gastos ?? 25);
  const [pctMin, setPctMin] = useState(defaults?.pct_margen_minimo ?? 30);
  const [pctSug, setPctSug] = useState(defaults?.pct_margen_sugerido ?? 60);
  const [costoBaseManual, setCostoBaseManual] = useState("");

  useEffect(() => {
    if (defaults) {
      setTrm(defaults.trm ?? 4200);
      setPctGastos(defaults.pct_gastos ?? 25);
      setPctMin(defaults.pct_margen_minimo ?? 30);
      setPctSug(defaults.pct_margen_sugerido ?? 60);
    }
  }, [defaults]);

  const baseCop = costoBaseManual !== ""
    ? Number(costoBaseManual) || 0
    : (Number(usd) || 0) * (Number(trm) || 0);
  const costoReal = Math.round(baseCop * (1 + (Number(pctGastos) || 0) / 100));
  const precioMin = Math.round(costoReal * (1 + (Number(pctMin) || 0) / 100));
  let precioSug = Math.round(costoReal * (1 + (Number(pctSug) || 0) / 100));
  if (precioSug < precioMin) precioSug = precioMin;

  return (
    <div className="col-span-2 border border-primary/20 rounded-lg bg-accent/40" data-testid="costeo-calculator">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-primary"
        data-testid="toggle-calculator"
      >
        <span className="flex items-center gap-2">
          <Calculator className="w-4 h-4" />
          Calculadora de costo real (TRM + gastos + margen)
        </span>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3">
          {defaults?.origen?.contenedor ? (
            <div className="text-[11px] rounded-md bg-emerald-50 dark:bg-emerald-900/20 text-emerald-800 dark:text-emerald-200 px-2 py-1.5" data-testid="costeo-origen">
              Valores del contenedor <b>{defaults.origen.contenedor}</b>{defaults.origen.invoice_no ? ` (${defaults.origen.invoice_no})` : ""}: TRM $ {Number(defaults.trm).toLocaleString("es-CO")} · gastos {defaults.pct_gastos}% — es la TRM con la que se costeó este lote.
            </div>
          ) : (
            <div className="text-[11px] rounded-md bg-muted/60 text-muted-foreground px-2 py-1.5" data-testid="costeo-origen-global">
              Valores globales de Configuración → Costeo (TRM $ {Number(defaults?.trm || 0).toLocaleString("es-CO")}). Solo son un punto de partida para la calculadora; no cambian el costo guardado del producto.
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Precio USD unitario</Label>
              <Input type="number" min="0" step="0.01" value={usd}
                onChange={(e) => { setUsd(e.target.value); setCostoBaseManual(""); }}
                className="h-10 mt-1" placeholder="Ej: 0.50" data-testid="calc-usd" />
            </div>
            <div>
              <Label className="text-xs">TRM (COP/USD)</Label>
              <Input type="number" min="0" step="0.01" value={trm}
                onChange={(e) => setTrm(e.target.value)}
                className="h-10 mt-1" data-testid="calc-trm" />
            </div>
          </div>
          <div className="text-[11px] text-muted-foreground -mt-1">
            O ingrese directamente el costo base en COP:
          </div>
          <div>
            <Label className="text-xs">Costo base COP (opcional, sobreescribe USD × TRM)</Label>
            <MoneyInput value={costoBaseManual}
              onValueChange={(v) => setCostoBaseManual(v)}
              className="h-10 mt-1" placeholder={baseCop ? String(Math.round(baseCop)) : "Auto"} data-testid="calc-base-manual" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">% Gastos</Label>
              <Input type="number" min="0" step="0.1" value={pctGastos}
                onChange={(e) => setPctGastos(e.target.value)}
                className="h-10 mt-1" data-testid="calc-pct-gastos" />
              <div className="text-[10px] text-muted-foreground mt-0.5">Flete, nacionalización, bodega</div>
            </div>
            <div>
              <Label className="text-xs">% Margen mín.</Label>
              <Input type="number" min="0" step="0.1" value={pctMin}
                onChange={(e) => setPctMin(e.target.value)}
                className="h-10 mt-1" data-testid="calc-pct-min" />
              <div className="text-[10px] text-muted-foreground mt-0.5">Ganancia mínima</div>
            </div>
            <div>
              <Label className="text-xs">% Margen sug.</Label>
              <Input type="number" min="0" step="0.1" value={pctSug}
                onChange={(e) => setPctSug(e.target.value)}
                className="h-10 mt-1" data-testid="calc-pct-sug" />
              <div className="text-[10px] text-muted-foreground mt-0.5">Ganancia sugerida</div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 pt-2 border-t border-primary/20">
            <div>
              <div className="text-[10px] uppercase text-muted-foreground font-bold">Costo real</div>
              <div className="text-lg font-semibold text-foreground tabular-nums" data-testid="calc-out-costo">{formatCOP(costoReal)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-muted-foreground font-bold">Precio mínimo</div>
              <div className="text-lg font-semibold text-amber-700 tabular-nums" data-testid="calc-out-min">{formatCOP(precioMin)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-muted-foreground font-bold">Precio sugerido</div>
              <div className="text-lg font-semibold text-primary tabular-nums" data-testid="calc-out-sug">{formatCOP(precioSug)}</div>
            </div>
          </div>

          <Button
            type="button"
            onClick={() => onApply?.({ costo: costoReal, precio_minimo: precioMin, precio_sugerido: precioSug })}
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-10"
            disabled={!costoReal}
            data-testid="calc-apply"
          >
            Aplicar al formulario
          </Button>
        </div>
      )}
    </div>
  );
}
